import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon, IconName, Kbd } from "../ui";
import { useTheme, THEMES } from "../stores/useTheme";
import { useCommandBar } from "../stores/useCommandBar";
import { listProjects, ProjectSummary } from "../api/projects";
import { listCustomers, LedgerCustomer } from "../api/ledger";

/* CommandBar — the ⌘K / Ctrl-K global palette. Native to the V0.2 src/ui
 * stack: token-driven (theme-following), keyboard-first, and the single place
 * that ties the new IA together — navigate, create, switch theme, open a tool,
 * jump to a recent project or a ledger customer. Mounted once in AppShell. */

interface Command {
  id: string;
  label: string;
  group: string;
  icon: IconName;
  hint?: string;
  keywords?: string;
  run: () => void;
}

const GROUP_ORDER = ["Navigate", "Create", "Open project", "Jump to ledger", "Tools", "Theme"];
const groupIdx = (g: string) => {
  const i = GROUP_ORDER.indexOf(g);
  return i < 0 ? 99 : i;
};

const isMac = typeof navigator !== "undefined" && /mac/i.test(navigator.platform);

/** Subsequence fuzzy score: lower is better, null if no match. Rewards
 *  contiguous runs and early matches so "gis" ranks "Open GIS Import" high. */
function score(query: string, text: string): number | null {
  const q = query.toLowerCase();
  const t = text.toLowerCase();
  if (!q) return 0;
  let ti = 0;
  let s = 0;
  let last = -2;
  for (const ch of q) {
    const idx = t.indexOf(ch, ti);
    if (idx < 0) return null;
    s += idx - ti; // penalize gaps
    if (idx !== last + 1) s += 1; // penalize non-contiguous
    last = idx;
    ti = idx + 1;
  }
  return s;
}

export default function CommandBar({ onNewProject }: { onNewProject: () => void }) {
  const navigate = useNavigate();
  const open = useCommandBar((s) => s.open);
  const setOpen = useCommandBar((s) => s.setOpen);
  const toggle = useCommandBar((s) => s.toggle);
  const prefill = useCommandBar((s) => s.prefill);
  const clearPrefill = useCommandBar((s) => s.clearPrefill);
  const theme = useTheme((s) => s.theme);
  const setTheme = useTheme((s) => s.setTheme);

  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [customers, setCustomers] = useState<LedgerCustomer[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);

  // Global ⌘K / Ctrl-K toggle (+ Esc to close). Mounted once with this component.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        toggle();
      } else if (e.key === "Escape" && useCommandBar.getState().open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggle, setOpen]);

  // On open: reset, focus, and refresh the dynamic command sources.
  useEffect(() => {
    if (!open) return;
    // Seed from a prefill (ask-popover handoff) if one was set, else clear.
    setQuery(prefill ?? "");
    if (prefill) clearPrefill();
    setActive(0);
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    listProjects().then((p) => setProjects(p.slice(0, 6))).catch(() => setProjects([]));
    listCustomers().then(setCustomers).catch(() => setCustomers([]));
    return () => clearTimeout(t);
  }, [open]);

  const commands = useMemo<Command[]>(() => {
    const go = (path: string) => () => navigate(path);
    const cmds: Command[] = [
      { id: "nav-projects", label: "Go to Projects", group: "Navigate", icon: "folder", keywords: "home landing", run: go("/") },
      { id: "nav-ledger", label: "Go to Ledger", group: "Navigate", icon: "table", keywords: "assets provenance system of record", run: go("/ledger") },
      { id: "nav-library", label: "Go to Library", group: "Navigate", icon: "book", keywords: "configs jurisdiction dot standards catalog", run: go("/library") },
      { id: "nav-doctor", label: "Go to Doctor's Office", group: "Navigate", icon: "medkit", keywords: "compliance diagnose ifc tpf exchange requirement validate findings", run: go("/doctor") },
      { id: "new-project", label: "New project", group: "Create", icon: "plus", hint: "from a source", keywords: "import create start", run: onNewProject },
      { id: "tool-builder", label: "Open Asset Builder", group: "Tools", icon: "spreadsheet", keywords: "excel spreadsheet xlsx yaml generate ifc", run: go("/builder") },
      { id: "tool-projects", label: "Manage Projects", group: "Tools", icon: "folder", keywords: "cityjson project create corridor alignment import export combined ifc", run: go("/projects") },
      { id: "tool-forge", label: "Open Forge", group: "Tools", icon: "cube", keywords: "parametric equipment modeler photo mesh ifc meshy revit family", run: go("/forge") },
      { id: "tool-gis", label: "Open GIS Import", group: "Tools", icon: "map", keywords: "rest polygon arcgis", run: go("/gis") },
      { id: "tool-cad", label: "Open CAD-to-BIM", group: "Tools", icon: "cad", keywords: "dxf classify layers", run: go("/cad") },
      { id: "tool-staging", label: "Open PDF Staging", group: "Tools", icon: "target", keywords: "pdf plans sign calibrate place bubble alignment sheet decoder", run: go("/staging") },
      { id: "tool-pavement", label: "Open Pavement", group: "Tools", icon: "layers", keywords: "courses footprint surface designs regions", run: go("/pavement") },
      { id: "tool-markings", label: "Open Markings", group: "Tools", icon: "layers", keywords: "pavement markings dxf paint stripe yellow faithful mutcd", run: go("/markings") },
      { id: "tool-curb", label: "Open Curb", group: "Tools", icon: "layers", keywords: "curb foc eop face of edge pavement gutter return knockdown kerb ifc", run: go("/curb") },
      { id: "tool-linear", label: "Open Linear Assets", group: "Tools", icon: "layers", keywords: "fence retaining wall dxf linear railing line ledger", run: go("/linear") },
    ];

    for (const p of projects) {
      cmds.push({
        id: `proj-${p.project_id}`,
        label: p.name || "Untitled project",
        group: "Open project",
        icon: "cube",
        hint: `${p.total_assets.toLocaleString()} assets`,
        keywords: `${p.dot} project workspace`,
        run: () => navigate(`/p/${p.project_id}`),
      });
    }

    for (const c of customers) {
      cmds.push({
        id: `cust-${c.customer_id}`,
        label: c.customer_id,
        group: "Jump to ledger",
        icon: "globe",
        hint: `${c.asset_count.toLocaleString()} assets`,
        keywords: "ledger customer jurisdiction agency",
        run: () => navigate(`/ledger?customer=${encodeURIComponent(c.customer_id)}`),
      });
    }

    for (const t of THEMES) {
      cmds.push({
        id: `theme-${t.id}`,
        label: `Theme: ${t.label}`,
        group: "Theme",
        icon: t.id === "light" ? "sun" : "moon",
        hint: theme === t.id ? "active" : undefined,
        keywords: "appearance dark color switch",
        run: () => setTheme(t.id),
      });
    }
    return cmds;
  }, [navigate, onNewProject, setTheme, theme, projects, customers]);

  // Flat list in display order — the array the active index walks.
  const visible = useMemo<Command[]>(() => {
    if (!query.trim()) {
      return [...commands].sort((a, b) => groupIdx(a.group) - groupIdx(b.group));
    }
    const scored: { c: Command; s: number }[] = [];
    for (const c of commands) {
      const s = score(query.trim(), `${c.label} ${c.keywords ?? ""}`);
      if (s != null) scored.push({ c, s });
    }
    return scored.sort((a, b) => a.s - b.s).map((x) => x.c);
  }, [commands, query]);

  useEffect(() => {
    if (active >= visible.length) setActive(Math.max(0, visible.length - 1));
  }, [visible, active]);

  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest" });
  }, [active]);

  if (!open) return null;

  const exec = (c: Command) => {
    setOpen(false);
    c.run();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, visible.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const c = visible[active];
      if (c) exec(c);
    }
  };

  const grouped = !query.trim();
  // For grouped rendering, walk groups in order and emit a header before the
  // first item of each. Active state keys off identity in `visible`.
  let lastGroup = "";

  return (
    <div
      onMouseDown={() => setOpen(false)}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 300,
        background: "oklch(0% 0 0 / 0.45)",
        backdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: "12vh",
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          width: 600,
          maxWidth: "calc(100vw - 32px)",
          maxHeight: "70vh",
          display: "flex",
          flexDirection: "column",
          background: "var(--bg-surface)",
          border: "1px solid var(--border-subtle)",
          borderRadius: "var(--r-xl)",
          boxShadow: "var(--shadow-pop)",
          overflow: "hidden",
        }}
      >
        {/* Search row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "14px 16px",
            borderBottom: "1px solid var(--border-subtle)",
          }}
        >
          <Icon name="search" size={17} color="var(--fg-tertiary)" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            onKeyDown={onKeyDown}
            placeholder="Search commands, projects, customers…"
            aria-label="Search commands, projects, and customers"
            style={{
              flex: 1,
              background: "transparent",
              border: "none",
              outline: "none",
              color: "var(--fg-primary)",
              fontSize: 15,
              fontFamily: "var(--font-sans)",
            }}
          />
          <Kbd>esc</Kbd>
        </div>

        {/* Results */}
        <div style={{ overflowY: "auto", padding: 6 }}>
          {visible.length === 0 && (
            <div style={{ padding: "28px 16px", textAlign: "center", color: "var(--fg-tertiary)", fontSize: 13 }}>
              No matches for “{query}”
            </div>
          )}
          {visible.map((c) => {
            const isActive = visible[active]?.id === c.id;
            const header = grouped && c.group !== lastGroup ? c.group : null;
            if (grouped) lastGroup = c.group;
            return (
              <React.Fragment key={c.id}>
                {header && (
                  <div
                    className="mono"
                    style={{
                      padding: "10px 10px 4px",
                      fontSize: 10,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: "var(--fg-muted)",
                    }}
                  >
                    {header}
                  </div>
                )}
                <button
                  ref={isActive ? activeRef : null}
                  onMouseEnter={() => setActive(visible.findIndex((v) => v.id === c.id))}
                  onClick={() => exec(c)}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 11,
                    padding: "9px 10px",
                    borderRadius: "var(--r-md)",
                    border: "1px solid transparent",
                    background: isActive ? "var(--accent-soft)" : "transparent",
                    color: isActive ? "var(--accent)" : "var(--fg-primary)",
                    textAlign: "left",
                    cursor: "pointer",
                  }}
                >
                  <Icon name={c.icon} size={16} color={isActive ? "var(--accent)" : "var(--fg-tertiary)"} />
                  <span style={{ flex: 1, fontSize: 13.5, fontWeight: 500 }}>{c.label}</span>
                  {c.hint && (
                    <span
                      className="mono"
                      style={{ fontSize: 11, color: isActive ? "var(--accent)" : "var(--fg-tertiary)" }}
                    >
                      {c.hint}
                    </span>
                  )}
                </button>
              </React.Fragment>
            );
          })}
        </div>

        {/* Footer hints */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            padding: "8px 14px",
            borderTop: "1px solid var(--border-subtle)",
            fontSize: 11,
            color: "var(--fg-tertiary)",
          }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd>
            navigate
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <Kbd>↵</Kbd>
            select
          </span>
          <span style={{ flex: 1 }} />
          <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <Kbd>{isMac ? "⌘" : "Ctrl"}</Kbd>
            <Kbd>K</Kbd>
            to toggle
          </span>
        </div>
      </div>
    </div>
  );
}
